import * as React from "react";
import { useEffect, useMemo, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate, useParams } from "react-router"
import { RoleType, StorageKey } from "../../App/enums"
import { formatDate } from "../../App/Helpers/Helpers"
import { selectTranslations } from "../../App/i18n/i18nSlice"
import SessionService from "../../App/SessionService"
import { RootState } from "../../App/store"
import { setPostStatus } from "../Room/RoomPost/RoomPostSlice"
import { NotificationsActiveIcon, NotificationsIcon } from "../Shared/Components/SvgIcons"
import UserNotificationDropdownItem from "./NotificationDropdownItem"
import { onMarkSingleOrganizationNotificationsAsRead } from "./OrganizationNotificationServiceRequest"
import { onMarkSingleUserNotificationsAsRead } from "./UserNotificationServiceRequest"

function parseDate(value?: string | null) {
    if (!value) {
        return null
    }

    const parsedDate = new Date(value)
    if (Number.isNaN(parsedDate.getTime())) {
        return null
    }

    return parsedDate
}

function NotificationsDropdown() {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const role = useSelector((state: RootState) => state.rolesSlice.RolesSlice.roles)
    const notifications = useSelector((state: RootState) => state.UserNotificationSlice.notifications)
    const translation = useSelector(selectTranslations)
    const [notificationsOpen, setNotificationsOpen] = useState(false)
    const { id, roomId } = useParams()
    const orgId = SessionService.getItem(StorageKey.organizationId)
    const notificationFilterNumber = 5
    let notificationRef = React.useRef<HTMLInputElement>(null)

    const sortedNotifications = useMemo(() => {
        return [...notifications].sort((firstNotification, secondNotification) => {
            const firstDate = parseDate(firstNotification.created_at)?.getTime() || 0
            const secondDate = parseDate(secondNotification.created_at)?.getTime() || 0
            return secondDate - firstDate
        })
    }, [notifications])

    const visibleNotifications = useMemo(() => {
        return sortedNotifications.slice(0, notificationFilterNumber)
    }, [sortedNotifications])

    const hasNotification = visibleNotifications.length > 0

    const hasNewNotifications = useMemo(() => {
        return notifications.some(notification => notification.read === false)
    }, [notifications])

    const groupedNotifications = useMemo(() => {
        const now = new Date()
        const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate())
        const weekStart = new Date(todayStart)
        weekStart.setDate(todayStart.getDate() - 6)

        const groups: Array<{ key: "today" | "week" | "older"; title: string; items: Array<{ item: typeof visibleNotifications[number]; index: number }> }> = []

        visibleNotifications.forEach((notification, index) => {
            const createdAt = parseDate(notification.created_at)
            let key: "today" | "week" | "older" = "older"

            if (createdAt) {
                const createdDateStart = new Date(createdAt.getFullYear(), createdAt.getMonth(), createdAt.getDate())
                if (createdDateStart.getTime() === todayStart.getTime()) {
                    key = "today"
                } else if (createdDateStart >= weekStart) {
                    key = "week"
                }
            }

            const title = key === "today" ? "Heute" : key === "week" ? "Diese Woche" : "Älter"
            const existingGroup = groups.find((group) => group.key === key)

            if (existingGroup) {
                existingGroup.items.push({ item: notification, index })
            } else {
                groups.push({
                    key,
                    title,
                    items: [{ item: notification, index }],
                })
            }
        })

        const order: Array<"today" | "week" | "older"> = ["today", "week", "older"]
        return groups.sort((firstGroup, secondGroup) => order.indexOf(firstGroup.key) - order.indexOf(secondGroup.key))
    }, [visibleNotifications])

    useEffect(() => {
        const onMouseDownHandler = (event: Event) => {
            if (notificationsOpen && notificationRef.current && !notificationRef.current.contains(event.target as HTMLElement)) {
                setNotificationsOpen(false)
            }
        }
        document.addEventListener("mousedown", onMouseDownHandler)

        return () => {
            document.removeEventListener("mousedown", onMouseDownHandler)
        }
    }, [notificationsOpen])

    const onRouteChange = () => {
        switch (role) {
            case RoleType.USER:
                const userPath = "/notifications"
                navigate(userPath)
                window.scrollTo(0, 0)
                break
            case RoleType.ORGANIZATION:
                if (id === orgId) {
                    const organizationPath = `/notifications/${id}`
                    navigate(organizationPath)
                    window.scrollTo(0, 0)
                } else {
                    const organizationPath = `/notifications/${orgId}`
                    navigate(organizationPath)
                    window.scrollTo(0, 0)
                }

                break
            default:
                break
        }
    }

    const onNotificationClick = (notificationId: number, path: string) => {
        navigate(`/${path}`)

        switch (role) {
            case RoleType.USER:
                dispatch<any>(onMarkSingleUserNotificationsAsRead(notificationId))
                break
            case RoleType.ORGANIZATION:
                dispatch<any>(onMarkSingleOrganizationNotificationsAsRead(notificationId, id!))
                break
            default:
                break
        }

        if (roomId) {
            dispatch(setPostStatus({ postsStatus: true, singularPostStatus: false, postedInRoomStatus: false }))
        }
        setNotificationsOpen(false)
    }

    const onLastItem = (index: number) => visibleNotifications.length - 1 === index

    return (
        <div className="header__menu-notifications" ref={notificationRef}>
            <div
                className="header__icon"
                onClick={() => {
                    setNotificationsOpen(!notificationsOpen)
                }}
            >
                {!hasNewNotifications ? <NotificationsIcon /> : <NotificationsActiveIcon />}
            </div>
            {notificationsOpen && (
                <div className="notifications-dropdown">
                    <div className="notifications-dropdown__content">
                        <div className="notifications-dropdown__upper-panel">
                            {hasNotification ? (
                                groupedNotifications.map((group) => {
                                    return (
                                        <div key={group.key} className="notifications-dropdown__group">
                                            <div className="notifications-dropdown__group-title">{group.title}</div>
                                            {group.items.map(({ item: notification, index }) => {
                                                return (
                                                    <UserNotificationDropdownItem
                                                        isLastItem={onLastItem(index)}
                                                        key={notification.id}
                                                        id={notification.id}
                                                        sender={notification!.content_type!}
                                                        photoSrc={notification.content_object?.photo?.url_path || notification.content_object?.profile_photo?.url_path}
                                                        itemSizeLarge={false}
                                                        first_name={notification.content_object?.first_name || translation.deletedUser}
                                                        last_name={notification.content_object?.last_name}
                                                        name={notification.content_object?.name || translation.deletedOrganization}
                                                        notification={notification.content}
                                                        date={formatDate(notification?.created_at!, "HH:mm dd. MMM")}
                                                        isRead={notification.read}
                                                        post={notification.post}
                                                        onClick={() => onNotificationClick(notification.id!, notification.redirect_url)}
                                                        redirect_url={notification.redirect_url}
                                                        action_url={notification?.action_url!}
                                                        roomId={notification.room}
                                                        invitation_id={notification.invitation_id}
                                                        isDropDown={true}
                                                    />
                                                )
                                            })}
                                        </div>
                                    )
                                })
                            ) : (
                                <div className="notifications-dropdown__empty-state">
                                    <div className="notifications-dropdown__empty-icon"><NotificationsIcon /></div>
                                    <div className="notifications-dropdown__text">{translation.youHaveNoNotifications}</div>
                                </div>
                            )}
                        </div>
                        <div className="notifications-dropdown__lower-panel">
                            <button type="button" className="notifications-dropdown__link" onClick={onRouteChange}>
                                {translation.seeAllResults}
                            </button>
                        </div>
                    </div>
                </div>
            )}{" "}
        </div>
    )
}

export default NotificationsDropdown
